import { ChecklistItem } from '@app/components/checklist';
import { useAppSelector } from '@app/store';
import { useTranslation } from '@hopin-team/provider-translation';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { AiStarLoader } from '../../../routes/lab/components/loader/ai-star-loader';
import { selectContentComposerContentRepurposingState } from '../selector';

/** Duration to show "All set!" message before transitioning to ready */
const COMPLETION_ANIMATION_DELAY_MS = 2000;

/** Translation keys for eligibility states */
const ELIGIBILITY_KEYS = {
  ANALYZING_TITLE:
    'content-recording.content-composer.eligibility.analyzing-title',
  ANALYZING_DESCRIPTION:
    'content-recording.content-composer.eligibility.analyzing-description',
  INELIGIBLE_TITLE:
    'content-recording.content-composer.eligibility.ineligible-title',
  INELIGIBLE_DESCRIPTION:
    'content-recording.content-composer.eligibility.ineligible-description',
  FAILED_TITLE: 'content-recording.content-composer.eligibility.failed-title',
  FAILED_DESCRIPTION:
    'content-recording.content-composer.eligibility.failed-description',
  LOADING_COMPLETED:
    'content-recording.content-composer.eligibility.loading-completed',
  MATCHING_TONE: 'content-recording.content-composer.eligibility.matching-tone',
  TONE_MATCHED: 'content-recording.content-composer.eligibility.tone-matched',
  USING_DEFAULT_TONE:
    'content-recording.content-composer.eligibility.using-default-tone',
  FINDING_TOPICS:
    'content-recording.content-composer.eligibility.finding-topics',
  FOUND_TOPICS: 'content-recording.content-composer.eligibility.found-topics',
} as const;

export type ContentComposerLoadingPhase =
  | 'loading'
  | 'post-processing'
  | 'ineligible'
  | 'failed'
  | 'ready';

export type LoaderLayoutProps = {
  icon?: React.ReactNode;
  title?: string;
  description?: string;
};

export type UseContentComposerLoadingStateReturn = {
  /** Current loading phase */
  phase: ContentComposerLoadingPhase;
  /** Props for LoaderLayout (null when ready) */
  loaderLayoutProps: LoaderLayoutProps | null;
  /** Checklist items for post-processing phase */
  checklistItems: ChecklistItem[];
};

/**
 * Hook that manages Content Composer loading state for recording eligibility.
 *
 * Note: License check (no-license) is handled by editor-page.tsx which renders
 * UpgradeBanner instead of Landing component. This hook only handles states
 * after the license check passes.
 *
 * ## Phase Flow
 * ```
 * loading ────► PENDING_CHECKS ────► INELIGIBLE ──► ineligible (show message)
 *                     │
 *                     ▼
 *               ELIGIBLE + processing ──► post-processing (show checklist)
 *                     │
 *                     ├─► topics FAILED ──► failed (blocking failure)
 *                     │
 *                     ▼
 *               all completed ─┬─ never saw processing ──► ready (skip animation)
 *                              │
 *                              └─ saw processing ──► "All set!" + checklist
 *                                                           │
 *                                                           ▼ (2s delay)
 *                                                         ready
 * ```
 *
 * ## Special Behaviors
 * - TOV failure is NOT blocking: marks as completed, shows "Using default tone"
 * - Topics failure IS blocking: shows failure state
 * - If user arrives with tone/topics already completed, skips directly to 'ready'
 * - If user watches processing happen, shows "All set!" with checklist for 2s before 'ready'
 *
 * @returns Loading state with phase, loaderLayoutProps, and checklistItems
 */
export function useContentComposerLoadingState(): UseContentComposerLoadingStateReturn {
  const { t } = useTranslation();
  const contentRepurposing = useAppSelector(
    selectContentComposerContentRepurposingState,
  );

  // State for managing the 2s delay after completion before showing ready
  const [isReadyAfterDelay, setIsReadyAfterDelay] = useState(false);

  // Tracks if user has seen the checklist in "processing" state
  // Used to skip the "All set!" animation if user arrives with everything already done
  const hasSeenProcessingRef = useRef(false);

  // --- Derived State ---
  const data = contentRepurposing.data;

  // Type-safe access: toneOfVoice and topics only exist when ELIGIBLE
  const toneOfVoiceStatus =
    data?.eligibility === 'ELIGIBLE'
      ? data.toneOfVoice?.status ?? 'FAILED'
      : undefined;
  const topicsStatus =
    data?.eligibility === 'ELIGIBLE' ? data.topics?.status : undefined;

  // Topics failure is a blocking failure - cannot proceed
  const topicsFailed = topicsStatus === 'FAILED';

  // TOV failure is NOT blocking - we use default tone instead
  // So "completed" means: TOV succeeded OR failed (using default), AND topics succeeded
  const isAllCompleted =
    (toneOfVoiceStatus === 'FINISHED' || toneOfVoiceStatus === 'FAILED') &&
    topicsStatus === 'FINISHED';

  // Processing = ELIGIBLE, not failed, and not yet all completed
  const isProcessing =
    data?.eligibility === 'ELIGIBLE' && !topicsFailed && !isAllCompleted;

  // --- Effects ---

  // Track when user sees the processing state (checklist animating)
  useEffect(() => {
    if (isProcessing) {
      hasSeenProcessingRef.current = true;
    }
  }, [isProcessing]);

  // Timer: Start 2s countdown when all completed (only if user saw processing)
  useEffect(() => {
    // Only start timer if:
    // 1. All items completed
    // 2. User saw the processing animation (otherwise skip to ready immediately)
    // 3. Haven't already transitioned to ready
    if (!isAllCompleted || !hasSeenProcessingRef.current || isReadyAfterDelay) {
      return;
    }

    const timer = setTimeout(() => {
      setIsReadyAfterDelay(true);
    }, COMPLETION_ANIMATION_DELAY_MS);

    return () => clearTimeout(timer);
  }, [isAllCompleted, isReadyAfterDelay]);

  // --- Phase Calculation ---
  const phase = useMemo((): ContentComposerLoadingPhase => {
    if (isProcessing) {
      return 'post-processing';
    }

    // 1. API still loading
    if (contentRepurposing.status !== 'success') {
      return 'loading';
    }

    // 2. Eligibility checks
    if (data?.eligibility === 'PENDING_CHECKS') {
      return 'loading';
    }

    if (data?.eligibility === 'INELIGIBLE') {
      return 'ineligible';
    }

    // 3. ELIGIBLE - check for failures and completion
    if (data?.eligibility === 'ELIGIBLE') {
      // Topics failure is blocking - show failure state
      if (topicsFailed) {
        return 'failed';
      }

      // Still processing tone/topics → show checklist
      if (!isAllCompleted) {
        return 'post-processing';
      }

      // All completed → check if we should show "All set!" animation
      // Skip animation if user arrived with everything already done
      if (!hasSeenProcessingRef.current) {
        return 'ready';
      }

      // User saw processing → show "All set!" until timer fires
      if (isReadyAfterDelay) {
        return 'ready';
      }

      return 'post-processing';
    }

    // Fallback
    return 'loading';
  }, [
    isProcessing,
    contentRepurposing.status,
    data?.eligibility,
    topicsFailed,
    isAllCompleted,
    isReadyAfterDelay,
  ]);

  // --- Checklist Items ---
  const checklistItems = useMemo((): ChecklistItem[] => {
    // Only show checklist in post-processing phase
    if (phase !== 'post-processing') {
      return [];
    }

    // Use API status directly, default to INITIAL if undefined
    const toneStatus = toneOfVoiceStatus ?? 'INITIAL';
    const topicStatus = topicsStatus ?? 'INITIAL';

    // Determine tone text key:
    // - FINISHED: "Tone matched!"
    // - FAILED: "Using default tone" (non-blocking, treated as completed)
    // - Otherwise: "Matching your tone..."
    const toneTextKey =
      toneStatus === 'FINISHED'
        ? ELIGIBILITY_KEYS.TONE_MATCHED
        : toneStatus === 'FAILED'
        ? ELIGIBILITY_KEYS.USING_DEFAULT_TONE
        : ELIGIBILITY_KEYS.MATCHING_TONE;

    // Determine topic text key:
    // - FINISHED: "Topics found!"
    // - Otherwise: "Finding key topics..."
    const topicTextKey =
      topicStatus === 'FINISHED'
        ? ELIGIBILITY_KEYS.FOUND_TOPICS
        : ELIGIBILITY_KEYS.FINDING_TOPICS;

    return [
      {
        id: 'tone',
        // If TOV failed, mark as FINISHED visually (we're using default tone)
        status: toneStatus === 'FAILED' ? 'FINISHED' : toneStatus,
        textKey: toneTextKey,
      },
      {
        id: 'topics',
        status: topicStatus,
        textKey: topicTextKey,
      },
    ];
  }, [phase, toneOfVoiceStatus, topicsStatus]);

  // --- Loader Layout Props ---
  const loaderLayoutProps = useMemo((): LoaderLayoutProps | null => {
    // No loader needed when ready
    if (phase === 'ready') {
      return null;
    }

    switch (phase) {
      case 'loading':
        return {
          icon: <AiStarLoader scale={0.75} showShine />,
          title: t(ELIGIBILITY_KEYS.ANALYZING_TITLE),
          description: t(ELIGIBILITY_KEYS.ANALYZING_DESCRIPTION),
        };

      case 'post-processing':
        // Show "All set!" message when all completed (checklist via rightContent)
        if (isAllCompleted) {
          return {
            icon: <AiStarLoader scale={0.75} showShine />,
            title: t(ELIGIBILITY_KEYS.LOADING_COMPLETED),
          };
        }
        // Still processing - show icon with title
        return {
          icon: <AiStarLoader scale={0.75} showShine />,
          title: t(ELIGIBILITY_KEYS.ANALYZING_TITLE),
        };

      case 'ineligible':
        return {
          title: t(ELIGIBILITY_KEYS.INELIGIBLE_TITLE),
          description: t(ELIGIBILITY_KEYS.INELIGIBLE_DESCRIPTION),
        };

      case 'failed':
        return {
          title: t(ELIGIBILITY_KEYS.FAILED_TITLE),
          description: t(ELIGIBILITY_KEYS.FAILED_DESCRIPTION),
        };

      default:
        return null;
    }
  }, [phase, isAllCompleted, t]);

  return {
    phase,
    loaderLayoutProps,
    checklistItems,
  };
}
